import { z } from "zod";
import type { MeNocStage } from "./noc-stages";
import type { GroupFacility, GroupScope, PermissionCode, UserGroupRef } from "./user-groups";

/**
 * Auth wire contract.
 *
 * Request schemas are declared here in snake_case and mirror
 * `apps/api/src/schemas/auth.schema.ts`. The frontend validates with the SAME
 * shape the server will validate with, so a mismatch is a typecheck failure at
 * build time instead of a 422 in front of a user.
 */

export const ROLE_CODES = ["SUPER_ADMIN", "ADMIN", "MEDAL_DESK", "OPERATION", "ORGANIZATION", "TRANSPORT_PROVIDER", "HOTEL", "WORKFORCE", "PARTICIPANT", "PUBLIC"] as const;
export type RoleCode = (typeof ROLE_CODES)[number];

/**
 * A person's own login, created by the database-backed registration path the
 * moment their FIRST accreditation card is created (user, 2026-11-03): one
 * per person (`people.user_id`), username = the card number digits
 * (`formatCardNumber`), a generated temporary password stored encrypted as
 * the record's UNIQUE CODE until they change it. Not scoped, not a group
 * role: every card / person predicate is literal FALSE for it. ACTIVE while
 * the person holds a live card (`PARTICIPANT_LIVE_CARD_STATUSES`), INACTIVE
 * otherwise — kept by a trigger on `ac_cards`.
 */
export const PARTICIPANT_ROLE = "PARTICIPANT" as const satisfies RoleCode;

/**
 * Roles whose access is LIMITED to what the login's USER GROUP grants: its
 * scope rows (organization, main function, sub-agency, sub-function, games),
 * its permissions and, for operators, its sports. The single source: the API
 * derives `isScoped` in its ROLE_DEFINITIONS and its SQL scope checks from this
 * list, and `GROUP_ROLES` (below) starts with it. A scoped role with no
 * group — or an inactive group, or a group of the other scoped role — sees
 * nothing.
 */
export const SCOPED_ROLES = ["OPERATION", "ORGANIZATION"] as const satisfies readonly RoleCode[];
export type ScopedRole = (typeof SCOPED_ROLES)[number];
export const isScopedRole = (code: RoleCode): code is ScopedRole => (SCOPED_ROLES as readonly RoleCode[]).includes(code);

/**
 * Roles that act through their login's user group too, but whose group holds
 * FACILITY rows (`user_group_facilities`: a transport provider, a lodging
 * building (M11 HOTEL); M12 a dining hall) instead of scope rows and sports. They are NOT
 * scoped roles: every card / person predicate (`cardInScope`,
 * `personInScope`, `targetInScope`, `cardWritable`, …) stays literal FALSE
 * for them with no code change, so a facility login never reads a card.
 * What they may see is decided by their own helpers (`providerOwns`,
 * `buildingInScope` in apps/api/src/db/queries/scope.ts). Like a scoped role, a facility role with
 * no ACTIVE group FOR that role gets nothing.
 */
export const FACILITY_ROLES = ["TRANSPORT_PROVIDER", "HOTEL"] as const satisfies readonly RoleCode[];
export type FacilityRole = (typeof FACILITY_ROLES)[number];
export const isFacilityRole = (code: RoleCode): code is FacilityRole => (FACILITY_ROLES as readonly RoleCode[]).includes(code);

/**
 * The roles a user group can be for: the scoped roles and the facility roles.
 * The database CHECK `ck_user_groups_role` is generated from it. Derived,
 * never re-typed (CLAUDE.md bug 4).
 */
export const GROUP_ROLES = [...SCOPED_ROLES, ...FACILITY_ROLES] as const;
export type GroupRole = (typeof GROUP_ROLES)[number];
export const isGroupRole = (code: RoleCode): code is GroupRole => (GROUP_ROLES as readonly RoleCode[]).includes(code);

/**
 * The GMS's STAFF roles: `GMS_ROLES` minus PARTICIPANT. Every API route fence
 * that used to read "any GMS role" reads this one, so adding PARTICIPANT to
 * the GMS opened no staff route to it (2026-11-03). A participant page opens
 * its own route, deliberately.
 */
export const GMS_STAFF_ROLES = ["SUPER_ADMIN", "ADMIN", "OPERATION", "ORGANIZATION", "TRANSPORT_PROVIDER", "HOTEL"] as const satisfies readonly RoleCode[];
export type GmsStaffRole = (typeof GMS_STAFF_ROLES)[number];

/**
 * Roles that may use the GMS (this web app) at all. Everyone else — WORKFORCE,
 * PUBLIC — signs in successfully but gets the "no access" page instead of the
 * app. The single definition: the web guard and the nav read it from here.
 * PARTICIPANT is a GMS role (their own pages); API fences use GMS_STAFF_ROLES.
 */
export const GMS_ROLES = [...GMS_STAFF_ROLES, PARTICIPANT_ROLE] as const satisfies readonly RoleCode[];
export type GmsRole = (typeof GMS_ROLES)[number];
export const isGmsRole = (code: RoleCode): code is GmsRole => (GMS_ROLES as readonly RoleCode[]).includes(code);

/** Roles that administer the GMS itself: user admin and reference lists. */
export const ADMIN_ROLES = ["SUPER_ADMIN", "ADMIN"] as const satisfies readonly RoleCode[];
export const isAdminRole = (code: RoleCode | null | undefined): boolean =>
	code != null && (ADMIN_ROLES as readonly RoleCode[]).includes(code);

/**
 * Whether acting as `role` gives this user anything to do in the GMS: a GMS
 * role, and — for a group role (scoped or facility) — a group that is ACTIVE
 * and is FOR that role (a group applies only while the active role equals its
 * `role_code`). A group role without one sees nothing: the SQL fails closed,
 * so the UI says so up front. The group belongs to the login, not the role,
 * so the same `group` is passed for every role the user holds.
 */
export function roleHasGmsAccess(
	role: RoleCode | null | undefined,
	group: Pick<UserGroupRef, "role_code" | "is_active"> | null | undefined,
): boolean {
	if (!role || !isGmsRole(role)) return false;
	// A PARTICIPANT gets its OWN shell (profile, events, medals), never the
	// staff one — the GMS's `_app` guard draws it; the API's `/me` routes are
	// the only ones open to it (staff fences read GMS_STAFF_ROLES).
	if (role === PARTICIPANT_ROLE) return true;
	if (!isGroupRole(role)) return true;
	return group != null && group.is_active && group.role_code === role;
}

/** The session-level check the web guard runs on `/auth/me`. */
export const hasGmsAccess = (me: Pick<MeResponse, "active_role" | "group">): boolean =>
	roleHasGmsAccess(me.active_role, me.group);

/**
 * Lower rank = more privilege. The single source: the API imports it for the
 * default-role pick, the escalation guard and the seeded `roles.rank`.
 * `roles.rank` is UNIQUE: every value differs (unit-tested). Facility roles
 * sit between the scoped roles and WORKFORCE: TRANSPORT_PROVIDER 36, M11's
 * HOTEL 37. PARTICIPANT (45) ranks below every staff role, so a staff login
 * that is also a participant never signs in as one by default.
 *
 * MEDAL_DESK (MSES, 25) ranks BELOW ADMIN on purpose: a login holding ADMIN
 * and MEDAL_DESK signs in to the GMS and OVR as ADMIN by default (lowest rank
 * wins), and MSES asks for MEDAL_DESK by name. It also means an ADMIN may
 * grant it (the escalation guard compares ranks).
 */
export const ROLE_RANK: Record<RoleCode, number> = {
	SUPER_ADMIN: 10,
	ADMIN: 20,
	MEDAL_DESK: 25,
	OPERATION: 30,
	ORGANIZATION: 35,
	TRANSPORT_PROVIDER: 36,
	HOTEL: 37,
	WORKFORCE: 40,
	PARTICIPANT: 45,
	PUBLIC: 50,
};

// ── Requests ────────────────────────────────────────────────────────────────
// Message-factory pattern (see `makeCreateUserForm` in ./users): each schema is
// built from a messages object with English defaults, so the API and the
// default exports keep the English wire copy while the web app passes
// translated copy (`apps/gms/src/lib/i18n/use-form-schemas.ts`).

export interface LoginFormMessages {
	identifierRequired: string;
	passwordRequired: string;
}
export const enLoginFormMessages: LoginFormMessages = {
	identifierRequired: "Enter your username or email",
	passwordRequired: "Enter your password",
};

/**
 * The staff apps that sign in through `POST /auth/login` (user, 2026-10-29:
 * track which app every sign-in came from). Sent as `client_app` in the
 * login body; stored on the attempt (`login_attempts.client_app`, CHECK
 * from this list) and carried through a 2FA step in the MFA ticket. A
 * label, not a credential: anyone can send any value, so it is never used
 * to decide access.
 */
export const CLIENT_APPS = ["GMS", "OVR", "MSES", "NOTION", "PORTAL"] as const;
export type ClientApp = (typeof CLIENT_APPS)[number];
/** The product names, shown as-is in every language. */
export const CLIENT_APP_NAMES: Record<ClientApp, string> = { GMS: "GMS", OVR: "OVR", MSES: "MSES", NOTION: "WSL Notion", PORTAL: "Web portal" };

export function makeLoginForm(m: LoginFormMessages = enLoginFormMessages) {
	return z.object({
		identifier: z.string().trim().min(1, m.identifierRequired).max(320),
		password: z.string().min(1, m.passwordRequired).max(200),
		role: z.enum(ROLE_CODES).optional(),
		/** Which app is signing in (`CLIENT_APPS`); absent = unknown (an API client, an older build). */
		client_app: z.enum(CLIENT_APPS).optional(),
	});
}
export const loginRequest = makeLoginForm();
export type LoginRequest = z.infer<typeof loginRequest>;

export interface MfaVerifyFormMessages {
	codeRequired: string;
}
export const enMfaVerifyFormMessages: MfaVerifyFormMessages = {
	codeRequired: "Enter the 6-digit code",
};

export function makeMfaVerifyForm(m: MfaVerifyFormMessages = enMfaVerifyFormMessages) {
	return z.object({
		mfa_ticket: z.string().min(20).max(2048),
		/** A 6-digit app code, or an 8-character backup code. */
		code: z.string().trim().min(6, m.codeRequired).max(12),
	});
}
export const mfaVerifyRequest = makeMfaVerifyForm();
export type MfaVerifyRequest = z.infer<typeof mfaVerifyRequest>;

export interface ChangePasswordFormMessages {
	currentPasswordRequired: string;
	newPasswordMin: string;
	newPasswordSame: string;
	/** The change-password PAGE's confirm box (`makeChangePasswordPageForm`). */
	confirmPasswordMismatch?: string;
}
export const enChangePasswordFormMessages: Required<ChangePasswordFormMessages> = {
	currentPasswordRequired: "Enter your current password",
	newPasswordMin: "At least 8 characters",
	newPasswordSame: "New password must be different from the current one",
	confirmPasswordMismatch: "The passwords do not match",
};

/**
 * The wire body of `POST /auth/change-password`. `current_password` is
 * optional: the FORCED first change (the session's token carries `pwc`)
 * does not ask for the temporary password again — the SERVER decides that
 * from the token, and refuses a voluntary change without it (422).
 */
export function makeChangePasswordForm(m: ChangePasswordFormMessages = enChangePasswordFormMessages) {
	return z
		.object({
			current_password: z.string().min(1, m.currentPasswordRequired).max(200).optional(),
			new_password: z.string().min(8, m.newPasswordMin).max(200),
		})
		.refine((v) => v.current_password === undefined || v.current_password !== v.new_password, {
			message: m.newPasswordSame,
			path: ["new_password"],
		});
}
export const changePasswordRequest = makeChangePasswordForm();
export type ChangePasswordRequest = z.infer<typeof changePasswordRequest>;

/**
 * The change-password PAGE: New + Confirm, plus Current unless `forced`
 * (`/auth/me.must_change_password`). Send `toChangePasswordBody(values)`.
 */
export function makeChangePasswordPageForm(forced: boolean, m: ChangePasswordFormMessages = enChangePasswordFormMessages) {
	const mismatch = m.confirmPasswordMismatch ?? enChangePasswordFormMessages.confirmPasswordMismatch;
	return z
		.object({
			current_password: forced
				? z.string().max(200).optional()
				: z.string().min(1, m.currentPasswordRequired).max(200),
			new_password: z.string().min(8, m.newPasswordMin).max(200),
			confirm_password: z.string().max(200),
		})
		.refine((v) => forced || v.current_password !== v.new_password, {
			message: m.newPasswordSame,
			path: ["new_password"],
		})
		.refine((v) => v.confirm_password === v.new_password, {
			message: mismatch,
			path: ["confirm_password"],
		});
}
export type ChangePasswordPageValues = z.infer<ReturnType<typeof makeChangePasswordPageForm>>;

/** The page's values → the wire body: no confirm box, no current password on the forced change. */
export function toChangePasswordBody(v: ChangePasswordPageValues, forced: boolean): ChangePasswordRequest {
	return forced || !v.current_password
		? { new_password: v.new_password }
		: { current_password: v.current_password, new_password: v.new_password };
}

// ── Responses ───────────────────────────────────────────────────────────────

export interface RoleSummary {
	code: RoleCode;
	name: string;
	rank: number;
	mfa_required: boolean;
}

export interface SelfUser {
	id: string;
	username: string;
	name: string;
	email: string | null;
	phone: string | null;
	status: "ACTIVE" | "INACTIVE" | "SUSPENDED";
}

/** Signed in. Carries tokens. */
export interface LoginSuccess {
	mfa_required: false;
	access_token: string;
	expires_in: number;
	refresh_token: string;
	refresh_expires_at: string;
	active_role: RoleCode;
	roles: RoleSummary[];
	must_change_password: boolean;
	mfa_enabled: boolean;
	user: SelfUser;
}

/**
 * Password accepted, second factor still owed. Carries NO tokens — the ticket
 * is a JWT for audience `<aud>:mfa` and can never pass `verifyAccessToken`.
 * `mfa_stage: "enroll"` means the role requires 2FA and this user has none yet.
 */
export interface MfaChallenge {
	mfa_required: true;
	mfa_stage: "verify" | "enroll";
	mfa_ticket: string;
	mfa_expires_in: number;
	active_role: RoleCode;
	user: SelfUser;
}

export type LoginResponse = LoginSuccess | MfaChallenge;

/** Narrowing helper — `mfa_required` is the discriminant. */
export const isMfaChallenge = (r: LoginResponse): r is MfaChallenge => r.mfa_required === true;

export interface RefreshResponse {
	access_token: string;
	expires_in: number;
	/** Absent in the grace-window case: the client keeps the token it has. */
	refresh_token?: string;
	active_role: RoleCode;
	must_change_password: boolean;
}

export interface MeResponse {
	user: SelfUser;
	roles: RoleSummary[];
	active_role: RoleCode | null;
	must_change_password: boolean;
	mfa_enabled: boolean;
	mfa_verified: boolean;
	/**
	 * The caller's user group with what it grants, or null when the login has
	 * none. Returned whatever the active role is; it APPLIES only while
	 * `active_role` equals `group.role_code` and the group is active (see
	 * `roleHasGmsAccess`). A UI convenience only — the server re-checks scope,
	 * permission and sport in SQL on every request.
	 */
	group: MeGroup | null;
	/**
	 * M7c: the NOC stage of this session and the modules it opens, or null when
	 * the active role is not ORGANIZATION (nothing else is ever staged). The
	 * server enforces it (403 STAGE_LOCKED); the GMS never re-derives it.
	 */
	noc_stage: MeNocStage | null;
	/**
	 * Merchandising: may this session use the merch desk? SUPER_ADMIN always;
	 * ADMIN when the login is on the merch staff list; every other role false.
	 * Read from the same SQL rule the `/merch` routes enforce — never re-derive it.
	 */
	merch_access: boolean;
	/**
	 * Change Logs: may this session open the page? SUPER_ADMIN always; another
	 * role when `changelog_settings.visible_roles` lists it. Never re-derive it.
	 */
	changelog_access: boolean;
	/**
	 * The groups this login may switch to from the role menu (user,
	 * 2026-10-29: one login acting as any one NOC at a time), the current one
	 * included; `[]` for a plain one-group login. Picking one sends
	 * `user_group_id` with `POST /auth/switch-role`.
	 */
	group_choices: MeGroupChoice[];
}

/** `/auth/me.group_choices`: a group to switch to, named by its organisation when its scope names exactly one. */
export interface MeGroupChoice {
	user_group_id: string;
	name: string;
	role_code: RoleCode;
	is_active: boolean;
	/** `code`: the organisation's country code (IOC) for its flag; null when it has none (the LOC). */
	organization: { id: string; name: string; code: string | null } | null;
}

/** `/auth/me`'s `group`: the reference plus everything the group grants. */
export type MeGroup = UserGroupRef & {
	permissions: PermissionCode[];
	sport_ids: string[];
	scopes: GroupScope[];
	/** M10/M11: a facility group's rows (a TRANSPORT_PROVIDER's provider, a HOTEL's buildings); `[]` for every other group. */
	facilities: GroupFacility[];
	/** The organisation, when the group's scope names exactly one (`MeGroupChoice`'s rule); `code` = its IOC code, for the flag. */
	organization: { id: string; name: string; code: string | null } | null;
};

export interface SwitchRoleResponse {
	access_token: string;
	expires_in: number;
	active_role: RoleCode;
}

export interface ChangePasswordResponse {
	access_token: string;
	expires_in: number;
}
